/**
 * VireoHR - Super Admin Tenant Management
 * Styled with Gosta design system
 * Features: Create tenant, Activate/Suspend, Edit plan, View stats
 */

import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import Input from '@/components/Input';
import Layout from '@/components/Layout';

import { collection, getDocs, orderBy, query, doc, updateDoc } from 'firebase/firestore';
import { firestore } from '@/lib/firebase/client';
import { adminCreateTenant, suspendTenant } from '@/services/cloudFunctions';

interface Tenant {
  id: string;
  name: string;
  ownerName: string;
  ownerEmail: string;
  status: 'active' | 'suspended' | 'trial';
  plan: 'trial' | 'basic' | 'premium';
  employeeCount: number;
  locationCount: number;
  createdAt: string;
  trialEnds?: string;
  subscriptionEnds?: string;
  monthlyPrice: number;
}

export default function TenantManagement() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [filter, setFilter] = useState<'all' | 'active' | 'trial' | 'suspended'>('all');
  const [loading, setLoading] = useState(true);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);

  // Form state
  const [businessName, setBusinessName] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchTenants();
  }, [filter]);

  const fetchTenants = async () => {
    try {
      setLoading(true);
      const q = query(collection(firestore, 'tenants'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);

      const tenantsData = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          name: data.name || 'Unnamed Business',
          ownerName: data.ownerName || 'Unknown Owner',  // Might need to join with users if not denormalized
          ownerEmail: data.ownerEmail,
          status: data.status,
          plan: data.plan,
          employeeCount: data.limits?.maxEmployees || 0, // Placeholder, real count needs subcollection count
          locationCount: data.limits?.maxBranches || 0,
          createdAt: data.createdAt?.toDate?.()?.toISOString().split('T')[0] || new Date().toISOString().split('T')[0],
          trialEnds: data.trialEnds?.toDate?.()?.toISOString().split('T')[0],
          monthlyPrice: data.plan === 'premium' ? 100 : (data.plan === 'basic' ? 50 : 0)
        } as Tenant;
      });

      // Filter locally if needed
      const filtered = filter === 'all'
        ? tenantsData
        : tenantsData.filter(t => t.status === filter);

      setTenants(filtered);
    } catch (error) {
      console.error('Failed to fetch tenants:', error);
      toast.error('Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTenant = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Real API Call
      const result = await adminCreateTenant({
        businessName,
        ownerName,
        ownerEmail,
        plan: 'trial' // Default to trial for now
      });

      const message = result.data.isNewUser
        ? `Tenant created! New user password: ${result.data.tempPassword}`
        : 'Tenant created successfully! Existing user promoted to owner.';

      toast.success(message, { duration: 8000 });

      // Reset form
      setBusinessName('');
      setOwnerName('');
      setOwnerEmail('');
      setShowCreateForm(false);

      fetchTenants();
    } catch (error) {
      console.error('Failed to create tenant:', error);
      toast.error('Failed to create tenant. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSuspend = async (tenantId: string) => {
    if (!confirm('Suspend this tenant? They will lose access to the system.')) return;

    try {
      await suspendTenant({ tenantId, suspend: true });
      toast.success('Tenant suspended successfully');
      fetchTenants(); // Refresh list
    } catch (error: any) {
      console.error('Failed to suspend tenant:', error);
      toast.error(`Failed to suspend: ${error.message}`);
    }
  };

  const handleActivate = async (tenantId: string) => {
    try {
      // Call suspendTenant with suspend=false to reactivate
      await suspendTenant({ tenantId, suspend: false });
      toast.success('Tenant activated successfully');
      fetchTenants(); // Refresh list
    } catch (error: any) {
      console.error('Failed to activate tenant:', error);
      toast.error(`Failed to activate tenant: ${error.message}`);
    }
  };

  const filteredTenants = filter === 'all'
    ? tenants
    : tenants.filter(t => t.status === filter);

  const activeCount = tenants.filter(t => t.status === 'active').length;
  const trialCount = tenants.filter(t => t.status === 'trial').length;
  const totalRevenue = tenants
    .filter(t => t.status === 'active')
    .reduce((sum, t) => sum + t.monthlyPrice, 0);

  return (<Layout title="Tenant Management">

    <div className="p-4 space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="text-center">
          <div className="text-3xl mb-2">🏢</div>
          <p className="text-2xl font-bold text-[#0D9488]">{tenants.length}</p>
          <p className="text-xs text-[#D4AF37]">Total Tenants</p>
        </Card>

        <Card className="text-center">
          <div className="text-3xl mb-2">✓</div>
          <p className="text-2xl font-bold text-[#0D9488]">{activeCount}</p>
          <p className="text-xs text-[#D4AF37]">Active</p>
        </Card>

        <Card className="text-center">
          <div className="text-3xl mb-2">⏱</div>
          <p className="text-2xl font-bold text-[#0D9488]">{trialCount}</p>
          <p className="text-xs text-[#D4AF37]">On Trial</p>
        </Card>

        <Card className="text-center">
          <div className="text-3xl mb-2">💰</div>
          <p className="text-2xl font-bold text-[#0D9488]">{totalRevenue}</p>
          <p className="text-xs text-[#D4AF37]">Monthly Revenue (JD)</p>
        </Card>
      </div>

      {/* Create Tenant Button */}
      <Card>
        <Button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="w-full"
        >
          {showCreateForm ? '✕ Cancel' : '➕ Create New Tenant'}
        </Button>
      </Card>

      {/* Create Form */}
      {showCreateForm && (
        <Card title="🏢 Create New Tenant">
          <form onSubmit={handleCreateTenant} className="space-y-4">
            <Input
              label="Business Name"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              placeholder="e.g., Café Downtown"
              required
            />

            <Input
              label="Owner Name"
              value={ownerName}
              onChange={(e) => setOwnerName(e.target.value)}
              placeholder="e.g., Ahmed Ali"
              required
            />

            <Input
              label="Owner Email"
              type="email"
              value={ownerEmail}
              onChange={(e) => setOwnerEmail(e.target.value)}
              placeholder="e.g., ahmed@example.com"
              required
            />

            <div className="p-3 bg-[#3B82F6] bg-opacity-10 rounded-lg">
              <p className="text-sm text-[#0D9488]">
                ℹ️ New tenant will start with a 30-day free trial
              </p>
            </div>

            <Button type="submit" disabled={submitting} className="w-full">
              {submitting ? '⏳ Creating...' : '✓ Create Tenant'}
            </Button>
          </form>
        </Card>
      )}

      {/* Filter Tabs */}
      <Card>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setFilter('all')}
            className={`
                px-4 py-2 rounded-lg font-semibold transition
                ${filter === 'all'
                ? 'bg-[#0D9488] text-[#D4AF37]'
                : 'bg-white text-[#0D9488] border-2 border-[#D4AF37]'}
              `}
          >
            All ({tenants.length})
          </button>

          <button
            onClick={() => setFilter('active')}
            className={`
                px-4 py-2 rounded-lg font-semibold transition
                ${filter === 'active'
                ? 'bg-[#0D9488] text-[#D4AF37]'
                : 'bg-white text-[#0D9488] border-2 border-[#D4AF37]'}
              `}
          >
            Active
          </button>

          <button
            onClick={() => setFilter('trial')}
            className={`
                px-4 py-2 rounded-lg font-semibold transition
                ${filter === 'trial'
                ? 'bg-[#0D9488] text-[#D4AF37]'
                : 'bg-white text-[#0D9488] border-2 border-[#D4AF37]'}
              `}
          >
            Trial
          </button>

          <button
            onClick={() => setFilter('suspended')}
            className={`
                px-4 py-2 rounded-lg font-semibold transition
                ${filter === 'suspended'
                ? 'bg-[#0D9488] text-[#D4AF37]'
                : 'bg-white text-[#0D9488] border-2 border-[#D4AF37]'}
              `}
          >
            Suspended
          </button>
        </div>
      </Card>

      {/* Tenant List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#0D9488] border-t-transparent"></div>
        </div>
      ) : filteredTenants.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🏢</div>
            <p className="text-xl font-semibold text-[#0D9488] mb-2">
              No {filter !== 'all' ? filter : ''} tenants
            </p>
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredTenants.map((tenant) => (
            <Card key={tenant.id}>
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold text-[#0D9488]">
                    {tenant.name}
                  </h3>
                  <p className="text-sm text-[#D4AF37]">
                    👤 {tenant.ownerName} • ✉️ {tenant.ownerEmail}
                  </p>
                </div>

                <Badge
                  variant={
                    tenant.status === 'active' ? 'success' :
                      tenant.status === 'suspended' ? 'error' : 'warning'
                  }
                >
                  {tenant.status === 'active' && '✓ Active'}
                  {tenant.status === 'suspended' && '⏸ Suspended'}
                  {tenant.status === 'trial' && '⏱ Trial'}
                </Badge>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="text-center p-3 bg-[#D4AF37] bg-opacity-10 rounded-lg">
                  <p className="text-2xl font-bold text-[#0D9488]">
                    {tenant.employeeCount}
                  </p>
                  <p className="text-xs text-[#D4AF37]">Employees</p>
                </div>

                <div className="text-center p-3 bg-[#D4AF37] bg-opacity-10 rounded-lg">
                  <p className="text-2xl font-bold text-[#0D9488]">
                    {tenant.locationCount}
                  </p>
                  <p className="text-xs text-[#D4AF37]">Locations</p>
                </div>

                <div className="text-center p-3 bg-[#D4AF37] bg-opacity-10 rounded-lg">
                  <p className="text-2xl font-bold text-[#0D9488]">
                    {tenant.monthlyPrice}
                  </p>
                  <p className="text-xs text-[#D4AF37]">JD/month</p>
                </div>
              </div>

              {/* Details */}
              <div className="space-y-2 text-sm mb-4">
                <div className="flex items-center justify-between">
                  <span className="text-[#D4AF37]">Created:</span>
                  <span className="text-[#0D9488] font-semibold">
                    {new Date(tenant.createdAt).toLocaleDateString()}
                  </span>
                </div>

                {tenant.trialEnds && (
                  <div className="flex items-center justify-between">
                    <span className="text-[#D4AF37]">Trial Ends:</span>
                    <span className="text-[#0D9488] font-semibold">
                      {new Date(tenant.trialEnds).toLocaleDateString()}
                    </span>
                  </div>
                )}

                {tenant.subscriptionEnds && (
                  <div className="flex items-center justify-between">
                    <span className="text-[#D4AF37]">Subscription Ends:</span>
                    <span className="text-[#0D9488] font-semibold">
                      {new Date(tenant.subscriptionEnds).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-4 border-t border-[#D4AF37]">
                {tenant.status === 'active' ? (
                  <Button
                    onClick={() => handleSuspend(tenant.id)}
                    variant="destructive"
                    className="flex-1"
                  >
                    ⏸ Suspend
                  </Button>
                ) : (
                  <Button
                    onClick={() => handleActivate(tenant.id)}
                    className="flex-1"
                  >
                    ✓ Activate
                  </Button>
                )}

                <Button
                  onClick={() => {
                    setEditingTenant(tenant);
                    setShowEditModal(true);
                  }}
                  variant="secondary"
                  className="flex-1"
                >
                  ✏️ Edit
                </Button>

                <Button
                  onClick={() => {
                    setSelectedTenant(tenant);
                    setShowDetailsModal(true);
                  }}
                  variant="secondary"
                >
                  👁️ Details
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Tenant Details Modal */}
      {showDetailsModal && selectedTenant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-2xl font-bold text-[#0D9488]">{selectedTenant.name}</h2>
                  <p className="text-[#D4AF37] text-sm mt-1">Tenant Details</p>
                </div>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Status Badge */}
              <div className="flex items-center gap-4">
                <Badge variant={
                  selectedTenant.status === 'active' ? 'success' :
                    selectedTenant.status === 'trial' ? 'warning' : 'error'
                }>
                  {selectedTenant.status.toUpperCase()}
                </Badge>
                <Badge variant="default">
                  {selectedTenant.plan.toUpperCase()}
                </Badge>
              </div>

              {/* Business Information */}
              <Card>
                <h3 className="text-lg font-semibold text-[#0D9488] mb-4">Business Information</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-[#D4AF37]">Tenant ID:</span>
                    <span className="font-mono text-sm">{selectedTenant.id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#D4AF37]">Business Name:</span>
                    <span className="font-semibold">{selectedTenant.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#D4AF37]">Created:</span>
                    <span>{new Date(selectedTenant.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>
              </Card>

              {/* Owner Information */}
              <Card>
                <h3 className="text-lg font-semibold text-[#0D9488] mb-4">Owner Information</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-[#D4AF37]">Name:</span>
                    <span className="font-semibold">{selectedTenant.ownerName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#D4AF37]">Email:</span>
                    <span>{selectedTenant.ownerEmail}</span>
                  </div>
                </div>
              </Card>

              {/* Usage Statistics */}
              <Card>
                <h3 className="text-lg font-semibold text-[#0D9488] mb-4">Usage Statistics</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-[#D4AF37]">👥 Employees:</span>
                    <span className="font-semibold">{selectedTenant.employeeCount}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#D4AF37]">📍 Locations:</span>
                    <span className="font-semibold">{selectedTenant.locationCount}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#D4AF37]">💰 Monthly Price:</span>
                    <span className="font-semibold text-[#0D9488]">
                      {selectedTenant.monthlyPrice} JOD
                    </span>
                  </div>
                </div>
              </Card>

              {/* Subscription Information */}
              <Card>
                <h3 className="text-lg font-semibold text-[#0D9488] mb-4">Subscription</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-[#D4AF37]">Plan:</span>
                    <span className="font-semibold capitalize">{selectedTenant.plan}</span>
                  </div>
                  {selectedTenant.trialEnds && (
                    <div className="flex justify-between">
                      <span className="text-[#D4AF37]">Trial Ends:</span>
                      <span>{new Date(selectedTenant.trialEnds).toLocaleDateString()}</span>
                    </div>
                  )}
                  {selectedTenant.subscriptionEnds && (
                    <div className="flex justify-between">
                      <span className="text-[#D4AF37]">Subscription Ends:</span>
                      <span>{new Date(selectedTenant.subscriptionEnds).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>
              </Card>
            </div>

            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-6">
              <Button
                onClick={() => setShowDetailsModal(false)}
                className="w-full"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Tenant Modal */}
      {showEditModal && editingTenant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-2xl font-bold text-[#0D9488]">Edit Tenant</h2>
                  <p className="text-[#D4AF37] text-sm mt-1">{editingTenant.name}</p>
                </div>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#D4AF37] mb-2">
                  Business Name
                </label>
                <Input
                  value={editingTenant.name}
                  onChange={(e) => setEditingTenant({ ...editingTenant, name: e.target.value })}
                  placeholder="Business name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#D4AF37] mb-2">
                  Owner Name
                </label>
                <Input
                  value={editingTenant.ownerName}
                  onChange={(e) => setEditingTenant({ ...editingTenant, ownerName: e.target.value })}
                  placeholder="Owner name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#D4AF37] mb-2">
                  Owner Email
                </label>
                <Input
                  type="email"
                  value={editingTenant.ownerEmail}
                  onChange={(e) => setEditingTenant({ ...editingTenant, ownerEmail: e.target.value })}
                  placeholder="owner@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#D4AF37] mb-2">
                  Plan
                </label>
                <select
                  value={editingTenant.plan}
                  onChange={(e) => setEditingTenant({ ...editingTenant, plan: e.target.value as any })}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#0D9488] focus:outline-none"
                >
                  <option value="trial">Trial</option>
                  <option value="basic">Basic</option>
                  <option value="premium">Premium</option>
                </select>
              </div>
            </div>

            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-6 flex gap-3">
              <Button
                onClick={() => setShowEditModal(false)}
                variant="secondary"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  try {
                    // Update tenant in Firestore
                    await updateDoc(doc(firestore, 'tenants', editingTenant.id), {
                      name: editingTenant.name,
                      ownerName: editingTenant.ownerName,
                      ownerEmail: editingTenant.ownerEmail,
                      plan: editingTenant.plan,
                      monthlyPrice: editingTenant.plan === 'premium' ? 100 : (editingTenant.plan === 'basic' ? 50 : 0),
                      updatedAt: new Date(),
                    });
                    toast.success(`Tenant "${editingTenant.name}" updated successfully!`);
                    setShowEditModal(false);
                    fetchTenants(); // Refresh the list
                  } catch (error: any) {
                    console.error('Failed to update tenant:', error);
                    toast.error(`Failed to update tenant: ${error.message}`);
                  }
                }}
                className="flex-1"
              >
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  </Layout>
  );
}
